import { useState, useCallback, useMemo, useEffect } from "react";
import { IDoorAssessment } from "../../../entities-generated/common/door-assessment";
import { IProjectAssessmentEditingModel, ProjectAssessmentEditingModel } from "../../../entities-generated/models/project/project-assessment-editing-model";
import { BwjInput } from "../../../framework/entities/bwj-input";
import { BwjValidityState } from "../../../framework/entities/bwj-validity-state";
import { EventNotificationService, useEventNotification } from "../../../framework/services/event-notification";
import { ValidatedFormValueService, MutableFormValueService, IFormValidityCheck } from "../../../framework/services/form-model-service-components";
import { NavigationService } from "../../../framework/services/navigation";
import { contentChanged, isNotNullOrWhitespace } from "../../../framework/utils";
import { any, none, where } from "../../../framework/utils/collection";
import { ProjectEvents } from "../events";
import { IAssessmentRecorderMainFormModel, IAssessmentRecorderMainValueModel, IAssessmentRecorderMainModelChange } from "./assess-record-main-iface";
import { ImageApiClient } from "../../../services/image";
import { IRecordAssessmentRequest } from "../../../entities-generated/models/project/record-assessment-request";
import { getAnnotatedCustomerNotes } from "../services/customer-note";
import { IDiscountRequest } from "../../../entities-generated/models/project/discount-request";
import { isEmptyString, isMeaningfulString } from "../../../framework/utils/string";
import { getUtcNowUnixTimestamp } from "../../../framework/utils/date-time";


// ##BEGIN:DEVELOPERCODE##

// this is a good place for doing things like defining custom form value service types

// ##END:DEVELOPERCODE##

// %%BEGIN:datePerformed/validated/number | null | undefined/number%%
export const datePerformed = new (class datePerformedService extends ValidatedFormValueService<IProjectAssessmentEditingModel, number | null | undefined, number> {
    public initialize(data: IProjectAssessmentEditingModel): void {
        this.setInputValue(new BwjInput<number | null | undefined>(data.datePerformed > 0 ? data.datePerformed : null));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: IProjectAssessmentEditingModel): void {
        data.datePerformed = this.getOutputValue();
    }

    public getDefaultValue(): BwjInput<number | null | undefined> {
        return new BwjInput<number | null | undefined>(null);
    }

    public getInputValue(): BwjInput<number | null | undefined> {
        return this.value;
    }

    public setInputValue(value: BwjInput<number | null | undefined>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): number {
        const value = this.value.value;
        return value ?? 0;
    }

    public validate(value: number | null | undefined): Promise<BwjInput<number | null | undefined>> {
        const result = this.requiredFieldValidator(value, 'Select the date of the assessment');
        return Promise.resolve(result);
    }

    public onUpdate(value: number | null | undefined): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

})(
    'datePerformed' // id
    ,'Assessment performed' // description
);
// %%END:datePerformed/validated/number | null | undefined/number%%

// %%BEGIN:commuteTimeBlocks/validated/string/number%%
export const commuteTimeBlocks = new (class commuteTimeBlocksService extends ValidatedFormValueService<IProjectAssessmentEditingModel, string, number> {
    public initialize(data: IProjectAssessmentEditingModel): void {
        this.setInputValue(new BwjInput<string>(data.commuteTimeBlocks > 0 ? data.commuteTimeBlocks.toString() : ''));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: IProjectAssessmentEditingModel): void {
        data.commuteTimeBlocks = this.getOutputValue();
    }

    public getDefaultValue(): BwjInput<string> {
        return new BwjInput<string>('');
    }

    public getInputValue(): BwjInput<string> {
        return this.value;
    }

    public setInputValue(value: BwjInput<string>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): number {
        const value = parseInt(this.value.value);
        return isNaN(value) ? 0 : value;
    }

    public validate(value: string): Promise<BwjInput<string>> {
        const result = this.requiredFieldValidator(value, 'Enter time for commute');
        return Promise.resolve(result);
    }

    public onUpdate(value: string): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

})(
    'commuteTimeBlocks' // id
    ,'Commute time (round trip)' // description
);
// %%END:commuteTimeBlocks/validated/string/number%%

// %%BEGIN:taxExemptionFormUrl/mutable/string/string%%
export const taxExemptionFormUrl = new (class taxExemptionFormUrlService extends MutableFormValueService<IProjectAssessmentEditingModel, string, string> {
    public initialize(data: IProjectAssessmentEditingModel): void {
        this.value = data.taxExemptionFormUrl;
    }

    public applyToDataModel(data: IProjectAssessmentEditingModel): void {
        data.taxExemptionFormUrl = this.value;
    }

    public getDefaultValue(): string {
        return '';
    }

    public getInputValue(): string {
        return this.value;
    }

    public setInputValue(value: string): void {
        this.value = value;
    }

    public getOutputValue(): string {
        return this.value;
    }

    public onUpdate(value: string): void {
        this.value = value;
        !!this.publishChange && this.publishChange(this.id);
    }
})(
    'taxExemptionFormUrl' // id
    ,'' // description
);
// %%END:taxExemptionFormUrl/mutable/string/string%%

// %%BEGIN:taxExemptionFormUrlUploader/validated/File[]/File[]%%
export const taxExemptionFormUrlUploader = new (class taxExemptionFormUrlUploaderService extends ValidatedFormValueService<IProjectAssessmentEditingModel, File[], File[]> {
    public initialize(data: IProjectAssessmentEditingModel): void {
        this.setInputValue(new BwjInput<File[]>([]));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: IProjectAssessmentEditingModel): void {}

    public getDefaultValue(): BwjInput<File[]> {
        return new BwjInput<File[]>([]);
    }

    public getInputValue(): BwjInput<File[]> {
        return this.value;
    }

    public setInputValue(value: BwjInput<File[]>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): File[] {
        return this.value.value;
    }

    public validate(value: File[]): Promise<BwjInput<File[]>> {
        const result = this.getInputObject(value);
        return Promise.resolve(result);
    }

    public onUpdate(value: File[]): void {
        const pendingResult = new BwjInput<File[]>(value);
        pendingResult.id = this.id;
        pendingResult.description = this.description;
        pendingResult.validity = BwjValidityState.Indeterminate;
        pendingResult.message = 'One moment please...';
        this.setInputValue(pendingResult);
        !!this.publishChange && this.publishChange(this.id);

        let exImg: string = taxExemptionFormUrl.getOutputValue();
        (isNotNullOrWhitespace(exImg) ? ImageApiClient.removeImage(exImg) : Promise.resolve())
        .then(() => true)
        .catch(() => {
            const verr = this.getDefaultValue();
            verr.validity = BwjValidityState.ValidationError;
            verr.message = 'Error occurred while preparing upload';
            this.setInputValue(verr);
            !!this.publishChange && this.publishChange(this.id);
            return false;
        })
        .then(ready => {
            if(ready) {
                taxExemptionFormUrl.setInputValue('');
                return ImageApiClient.uploadImage(value);
            }
            return undefined;
        })
        .then(resp => {
            if(!resp) { return; }
            taxExemptionFormUrl.setInputValue(resp.imageUrls[0]);

            const result = this.getDefaultValue();
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id);
        })
        .catch(() => {
            const verr = this.getDefaultValue();
            verr.validity = BwjValidityState.ValidationError;
            verr.message = 'Image upload failed';
            this.setInputValue(verr);
            !!this.publishChange && this.publishChange(this.id);
        })
    }
})(
    'taxExemptionFormUrlUploader' // id
    ,'State tax exemption form' // description
);
// %%END:taxExemptionFormUrlUploader/validated/File[]/File[]%%

// %%BEGIN:notes/validated/string/string%%
export const notes = new (class notesService extends ValidatedFormValueService<IProjectAssessmentEditingModel, string, string> {
    public initialize(data: IProjectAssessmentEditingModel): void {
        this.setInputValue(new BwjInput<string>(data.notes));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: IProjectAssessmentEditingModel): void {
        data.notes = this.value.value;
    }

    public getDefaultValue(): BwjInput<string> {
        return new BwjInput<string>('');
    }

    public getInputValue(): BwjInput<string> {
        return this.value;
    }

    public setInputValue(value: BwjInput<string>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): string {
        return this.value.value;
    }

    public validate(value: string): Promise<BwjInput<string>> {
        const result = this.getInputObject(value);
        return Promise.resolve(result);
    }

    public onUpdate(value: string): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

})(
    'notes' // id
    ,'Assessment notes' // description
);
// %%END:notes/validated/string/string%%

// %%BEGIN:customerNotes/validated/string/string%%
export const customerNotes = new (class customerNotesService extends ValidatedFormValueService<IProjectAssessmentEditingModel, string, string> {
    public initialize(data: IProjectAssessmentEditingModel): void {
        this.setInputValue(new BwjInput<string>(''));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: IProjectAssessmentEditingModel): void {}

    public getDefaultValue(): BwjInput<string> {
        return new BwjInput<string>('');
    }

    public getInputValue(): BwjInput<string> {
        return this.value;
    }

    public setInputValue(value: BwjInput<string>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): string {
        return this.value.value;
    }

    public validate(value: string): Promise<BwjInput<string>> {
        const result = this.getInputObject(value);
        return Promise.resolve(result);
    }

    public onUpdate(value: string): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

})(
    'customerNotes' // id
    ,'Customer profile notes' // description
);
// %%END:customerNotes/validated/string/string%%

// %%BEGIN:installationAssessments/validated/IDoorAssessment[]/IDoorAssessment[]%%
export const installationAssessments = new (class installationAssessmentsService extends ValidatedFormValueService<IProjectAssessmentEditingModel, IDoorAssessment[], IDoorAssessment[]> {
    public initialize(data: IProjectAssessmentEditingModel): void {
        this.setInputValue(new BwjInput<IDoorAssessment[]>(data.installationAssessments));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: IProjectAssessmentEditingModel): void {
        data.installationAssessments = this.value.value;
    }

    public getDefaultValue(): BwjInput<IDoorAssessment[]> {
        return new BwjInput<IDoorAssessment[]>([]);
    }

    public getInputValue(): BwjInput<IDoorAssessment[]> {
        return this.value;
    }

    public setInputValue(value: BwjInput<IDoorAssessment[]>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): IDoorAssessment[] {
        return this.value.value;
    }

    public validate(value: IDoorAssessment[]): Promise<BwjInput<IDoorAssessment[]>> {
        const result = this.getInputObject(value);
        if(value.length === 0) {
            result.validity = BwjValidityState.Invalid;
            result.message = 'Enter at least one assessment';
        }
        return Promise.resolve(result);
    }

    public onUpdate(value: IDoorAssessment[]): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

})(
    'installationAssessments' // id
    ,'Entryway Assessments' // description
);
// %%END:installationAssessments/validated/IDoorAssessment[]/IDoorAssessment[]%%

// %%BEGIN:discounts/validated/string/IDiscountRequest[]%%
export const discounts = new (class discountsService extends ValidatedFormValueService<IProjectAssessmentEditingModel, string, IDiscountRequest[]> {
    public initialize(data: IProjectAssessmentEditingModel): void {
        const strDiscounts = data.discounts.map(d => {
            const exp = new Date(d.expiresUtc);
            const flat = d.flatAmount !== 0 ? `|${d.flatAmount}` : '';
            const pct = d.percentage !== 0 ? `%${d.percentage}` : '';
            const line = `${d.code}${pct}${flat}>${exp.getFullYear()}-${(exp.getMonth() + 1)}-${exp.getDate()}`;
            return line;
        });
        this.setInputValue(new BwjInput<string>(strDiscounts.join('\n')));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: IProjectAssessmentEditingModel): void {
        data.discounts = this.getOutputValue();
    }

    public getDefaultValue(): BwjInput<string> {
        return new BwjInput<string>('');
    }

    public getInputValue(): BwjInput<string> {
        return this.value;
    }

    public setInputValue(value: BwjInput<string>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): IDiscountRequest[] {
        const arr = this.value.value.split('\n').map(x => this.toDiscountRequest(x)) as any[];
        const value = where(arr, x => !!x && !x.err) as IDiscountRequest[];
        return value;
    }

    public validate(value: string): Promise<BwjInput<string>> {
        const result = this.getInputObject(value);
        if(isMeaningfulString(value)) {
            const all = value.split('\n').map(x => this.toDiscountRequest(x)) as any[];
            const errs = where(all, x => !!x && !!x.err) as { err: string }[];
            if(errs.length > 0) {
                result.validity = BwjValidityState.Invalid;
                result.message = errs[0].err;
            }
        }
        return Promise.resolve(result);
    }

    public onUpdate(value: string): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

    private toDiscountRequest(value: string) {
        if(isEmptyString(value)) { return null; }
        const req: IDiscountRequest = {
            code: '',
            flatAmount: 0,
            percentage: 0,
            expiresUtc: 0
        };
        let parts = value.split('>');
        if(parts.length !== 2) { return { err: 'Multiple date separators encountered' }; }

        const arrDate = parts[1].split('-').map(x => parseInt(x));
        if(arrDate.length !== 3) { return { err: 'Date format must be YYYY-M-D' }; }
        if(any(arrDate, x => isNaN(x))) { return { err: 'Part of date is invalid' }; }
        req.expiresUtc = (new Date(arrDate[0], arrDate[1] - 1, arrDate[2])).getTime();
        if(req.expiresUtc < getUtcNowUnixTimestamp()) { return { err: 'Expiration date already expired' }; }

        if(parts[0].indexOf('|') !== -1) {
            parts = parts[0].split('|');
            if(parts[1].indexOf('%') !== -1) { return { err: 'Flat amount must be specified after a percentage value' } }
            const flat = parseInt(parts[1]);
            if(isNaN(flat)) { return { err: 'Flat amount is an invalid number' }; }
            req.flatAmount = flat;
        }

        if(parts[0].indexOf('%') !== -1) {
            parts = parts[0].split('%');
            const pct = parseInt(parts[1]);
            if(isNaN(pct)) { return { err: 'Percentage amount is an invalid number' }; }
            req.percentage = pct;
        }

        if(req.flatAmount === 0 && req.percentage === 0) { return { err: 'Discount amount is missing' }; }
        if(isEmptyString(parts[0])) { return { err: 'Discount has no description' }; }
        req.code = parts[0].trim();

        return req;
    }
})(
    'discounts' // id
    ,'Discounts' // description
);
// %%END:discounts/validated/string/IDiscountRequest[]%%

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const toFormModel = (): IAssessmentRecorderMainFormModel => {
    return {
       datePerformed: datePerformed.getInputValue(),
       commuteTimeBlocks: commuteTimeBlocks.getInputValue(),
       taxExemptionFormUrl: taxExemptionFormUrl.getInputValue(),
       taxExemptionFormUrlUploader: taxExemptionFormUrlUploader.getInputValue(),
       notes: notes.getInputValue(),
       customerNotes: customerNotes.getInputValue(),
       installationAssessments: installationAssessments.getInputValue(),
       discounts: discounts.getInputValue(),
    };
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const toValueModel = (): IAssessmentRecorderMainValueModel => {
    return {
       datePerformed: datePerformed.getOutputValue(),
       commuteTimeBlocks: commuteTimeBlocks.getOutputValue(),
       taxExemptionFormUrl: taxExemptionFormUrl.getOutputValue(),
       taxExemptionFormUrlUploader: taxExemptionFormUrlUploader.getOutputValue(),
       notes: notes.getOutputValue(),
       customerNotes: customerNotes.getOutputValue(),
       installationAssessments: installationAssessments.getOutputValue(),
       discounts: discounts.getOutputValue(),
    };
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
const getValidatedInputs = () => {
    return [
        datePerformed.getInputValue(),
        commuteTimeBlocks.getInputValue(),
        taxExemptionFormUrlUploader.getInputValue(),
        notes.getInputValue(),
        customerNotes.getInputValue(),
        installationAssessments.getInputValue(),
        discounts.getInputValue(),
    ];
}

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const isFormValid = () => none(getValidatedInputs(), (i) => i.validity === BwjValidityState.Invalid);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const getFormErrors = () => where(getValidatedInputs(), (i) => i.validity === BwjValidityState.Invalid);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
const broadcastChange = (propertyName: string) => {
    const arg: IAssessmentRecorderMainModelChange = {
        propertyChanged: propertyName,
        model: toFormModel()
    };

    EventNotificationService.dispatch(ProjectEvents.AssessRecordMainModelDidUpdate, arg);
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const validateAndUpdateModel = (): Promise<IFormValidityCheck<IAssessmentRecorderMainFormModel>> => {
    const promises = [
        datePerformed.formValidateAndUpdate(),
        commuteTimeBlocks.formValidateAndUpdate(),
        taxExemptionFormUrlUploader.formValidateAndUpdate(),
        notes.formValidateAndUpdate(),
        customerNotes.formValidateAndUpdate(),
        installationAssessments.formValidateAndUpdate(),
        discounts.formValidateAndUpdate(),
    ];

    return Promise.all(promises)
        .then(() => { 
            broadcastChange('validateAndUpdateModel:IAssessmentRecorderMainFormModel');
            return { isValid: isFormValid(), model: toFormModel() };
        });
}

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const isModelChanged = () => {
    if(initialState.valueModel === undefined) { return false; }
    const current = toValueModel();
    return contentChanged(initialState.valueModel, current);
}

// WARNING: GENERATED CODE -- changes will be lost on regeneration
const initialState: { valueModel?: IAssessmentRecorderMainValueModel, dataModel: IProjectAssessmentEditingModel } = {
    dataModel: new ProjectAssessmentEditingModel()
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const initializeService = (data?: IProjectAssessmentEditingModel) => {
    const dataModel = data || new ProjectAssessmentEditingModel();

    datePerformed.initialize(dataModel);
    commuteTimeBlocks.initialize(dataModel);
    taxExemptionFormUrl.initialize(dataModel);
    taxExemptionFormUrlUploader.initialize(dataModel);
    notes.initialize(dataModel);
    customerNotes.initialize(dataModel);
    installationAssessments.initialize(dataModel);
    discounts.initialize(dataModel);

    initialState.valueModel = toValueModel();
    initialState.dataModel = dataModel;
    broadcastChange('initializeService:IAssessmentRecorderMainFormModel');
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
datePerformed.setChangePublisher(broadcastChange);
commuteTimeBlocks.setChangePublisher(broadcastChange);
taxExemptionFormUrl.setChangePublisher(broadcastChange);
taxExemptionFormUrlUploader.setChangePublisher(broadcastChange);
notes.setChangePublisher(broadcastChange);
customerNotes.setChangePublisher(broadcastChange);
installationAssessments.setChangePublisher(broadcastChange);
discounts.setChangePublisher(broadcastChange);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const setReversionState = () => {
    const dataModel = new ProjectAssessmentEditingModel();

    datePerformed.applyToDataModel(dataModel);
    commuteTimeBlocks.applyToDataModel(dataModel);
    taxExemptionFormUrl.applyToDataModel(dataModel);
    taxExemptionFormUrlUploader.applyToDataModel(dataModel);
    notes.applyToDataModel(dataModel);
    customerNotes.applyToDataModel(dataModel);
    installationAssessments.applyToDataModel(dataModel);
    discounts.applyToDataModel(dataModel);

    initialState.valueModel = toValueModel();
    initialState.dataModel = dataModel;
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const revertChanges = () => {
    initializeService(initialState.dataModel);
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const useFormModelChangeEvent = (onChangeWrappedInUseCallback: ((change: IAssessmentRecorderMainModelChange) => void), subscriberId: string) =>
    useEventNotification(onChangeWrappedInUseCallback, [onChangeWrappedInUseCallback], ProjectEvents.AssessRecordMainModelDidUpdate, subscriberId);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const useFormModel = (subscriberId: string): IAssessmentRecorderMainFormModel => {
    const [getter, setter] = useState<IAssessmentRecorderMainFormModel>(toFormModel());

    useFormModelChangeEvent(useCallback((change: IAssessmentRecorderMainModelChange) => setter(change.model), []), subscriberId);

    return getter;
}
// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const useHasUnsavedChanges = (model: IAssessmentRecorderMainFormModel, warnOnUnsavedPageNavigation: boolean = false) => {
    const hasUnsavedChanges = useMemo(isModelChanged, [model]);

    useEffect(() => {
        if(warnOnUnsavedPageNavigation === false || hasUnsavedChanges === false) { return; }

        const unloadCallback = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            event.returnValue = '';
            return '';
        };

        window.addEventListener('beforeunload', unloadCallback);
        NavigationService.registerObjection('AssessmentRecorderMainFormService');

        return () => {
            window.removeEventListener('beforeunload', unloadCallback);
            NavigationService.rescindObjection('AssessmentRecorderMainFormService');
        }
    }, [warnOnUnsavedPageNavigation, hasUnsavedChanges]);

    return hasUnsavedChanges;
}
// ##BEGIN:DEVELOPERCODE##

export const toRecordRequest = (rejectProject: boolean) => {
    const v = toValueModel();
    const request: IRecordAssessmentRequest = {
        datePerformed: v.datePerformed,
        commuteTimeBlocks: v.commuteTimeBlocks,
        installationAssessments: v.installationAssessments,
        notes: v.notes,
        discounts: v.discounts,
        projectRejected: rejectProject,
        assessmentCancelled: false,
        customerNotes: getAnnotatedCustomerNotes(v.customerNotes, 'Recording assessment'),
        taxExemptionFormUrl: v.taxExemptionFormUrl
    };
    return request;
}

// ##END:DEVELOPERCODE##
